import { EVENTS, publish } from '@nutima-themes/utils/pubsub'

let cartState = null

export const getCartState = () => cartState
export const setCartState = (newState) => {
  cartState = newState
}

// Helper functions
const fetchCartData = async (endpoint, options = {}, productData = {}) => {
  try {
    dispatchCartEvent(EVENTS.cartBeforeChange, { detail: { endpoint, options } })

    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      if (errorData?.description) {
        throw {
          message: errorData.description,
          code: response.status
        }
      } else {
        throw {
          message: `Cart error: ${response.status} ${response.statusText}`,
          code: response.status
        }
      }
    }

    const data = await response.json()
    const cartReq = await fetch('/cart.js')
    const cartData = await cartReq.json()
    setCartState(cartData)
    dispatchCartEvent(EVENTS.cartChange, { detail: { endpoint, options, cart: data, cartState: cartData } })

    if ((endpoint == '/cart/add.js' || endpoint == '/cart/change.js') && productData.productHandle) {
      const req = await fetch(`/products/${productData.productHandle}.json`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const res = await req.json()
      if (res.product.tags.includes('max_qty_')) {
        const tags = res.product.tags.split(', ')
        const maxQty = tags.find((tag) => tag.includes('max_qty_')).replace('max_qty_', '')
        const lineItems = await fetchCartData('/cart.js')
        const items = lineItems.items.filter((item) => item.product_id == res.product.id)
        const item = items.find((item) => item.variant_id == productData.variantId)

        let quantity = 0

        items.forEach((item) => {
          quantity += item.quantity
        })

        const updates = {}
        let lastQty = 0
        let quota = parseInt(maxQty)
        items.forEach((item) => {
          quota = quota - item.quantity
          if (quota < 0) {
            updates[item.id] = quota + item.quantity
          } else if (quota >= 0) {
            updates[item.id] = item.quantity
          } else {
            updates[item.id] = 0
          }
        })

        await updateQuantity(updates, productData.sections)

        await getCart()
      }
    }

    return data
  } catch (error) {
    dispatchCartEvent(EVENTS.cartError, { detail: { endpoint, options } })
    console.error('Cart operation failed:', error)
    throw error
  }
}

const dispatchCartEvent = (eventName, data) => {
  publish(eventName, data)
}

// Cart operations
export const getCart = () => fetchCartData('/cart.js')

export const addItem = (variantId, quantity = 1, sellingPlan = null, productHandle = false) => {
  const sections = document.querySelector('n-drawer-cart')?.id || document.querySelector('n-main-cart')?.id
  
  const item = {
    id: variantId,
    quantity
  }
  
  // Add selling plan if provided
  if (sellingPlan) {
    item.selling_plan = sellingPlan
  }
  
  return fetchCartData(
    '/cart/add.js',
    {
      method: 'POST',
      body: JSON.stringify({
        items: [item],
        sections
      })
    },
    {
      productHandle,
      variantId,
      quantity,
      sellingPlan,
      sections
    }
  )
}

export const addItems = (items, sections) => {
  if (!sections) {
    sections = document.querySelector('n-drawer-cart')?.id || document.querySelector('n-main-cart')?.id
  }
  fetchCartData('/cart/add.js', {
    method: 'POST',
    body: JSON.stringify({ items, sections })
  })
}

export const updateItem = (itemId, quantity, sections, productHandle) => {
  if (!sections) {
    sections = document.querySelector('n-drawer-cart')?.id || document.querySelector('n-main-cart')?.id
  }
  
  const requestBody = {
    id: itemId,
    quantity,
    sections
  }
  
  return fetchCartData(
    '/cart/change.js',
    {
      method: 'POST',
      body: JSON.stringify(requestBody)
    },
    {
      productHandle,
      itemId,
      quantity,
      sections
    }
  )
}

export const removeItem = (variantId) => updateItem(variantId, 0)

export const clearCart = (sections) => {
  if (!sections) {
    sections = document.querySelector('n-drawer-cart')?.id || document.querySelector('n-main-cart')?.id
  }
  return fetchCartData('/cart/clear.js', {
    method: 'POST',
    body: JSON.stringify({ sections })
  })
}

export const updateQuantity = (updates, sections) => {
  if (!sections) {
    sections = document.querySelector('n-drawer-cart')?.id || document.querySelector('n-main-cart')?.id
  }
  return fetchCartData('/cart/update.js', {
    method: 'POST',
    body: JSON.stringify({
      updates,
      sections
    })
  })
}

export const updateNote = (note) =>
  fetchCartData('/cart/update.js', {
    method: 'POST',
    body: JSON.stringify({ note })
  })

export const getShippingRates = async () => {
  try {
    const response = await fetch('/cart/shipping_rates.json', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    return await response.json()
  } catch (error) {
    console.error('Failed to get shipping rates:', error)
    throw error
  }
}

// Helper methods
export const getItemCount = () => cartState?.item_count || 0
export const getTotal = () => cartState?.total_price || 0
export const getItems = () => cartState?.items || []

// Discount operations
export const applyDiscount = async (code) => {
  try {
    const response = await fetch('/discount/' + code, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error('Invalid discount code')
    }

    await getCart()
    return true
  } catch (error) {
    console.error('Failed to apply discount:', error)
    throw error
  }
}

export const removeDiscount = async () => {
  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        discount_code: ''
      })
    })

    await getCart()
    return true
  } catch (error) {
    console.error('Failed to remove discount:', error)
    throw error
  }
}
